import type { ViewNode } from "@e308/ux";
import { decodeOnlineRoomSave } from "../online-room-save.js";
import type { AppIntent } from "./app-source.js";

/* v8 ignore start -- interactive browser launcher is exercised by Playwright */
export function onlineRoomLauncher(): ViewNode<AppIntent, string> {
  return {
    kind: "custom",
    id: "online-room-launcher",
    render: (document) => renderLauncher(document),
  };
}

function renderLauncher(document: Document): HTMLElement {
  const section = document.createElement("section");
  section.className = "kf-online e308-section-card";
  const heading = document.createElement("h2");
  heading.textContent = "Online room";
  const note = document.createElement("p");
  note.textContent =
    "Share a shift with friends. Create a room and send its invite link, or enter a room code to join.";
  const room = document.createElement("input");
  room.value = `room-${crypto.randomUUID().slice(0, 8)}`;
  room.maxLength = 64;
  room.setAttribute("aria-label", "Room code");
  const roomError = document.createElement("p");
  roomError.id = "room-code-error";
  roomError.setAttribute("role", "alert");
  room.setAttribute("aria-describedby", roomError.id);
  room.addEventListener("input", () => {
    roomError.textContent = "";
    room.removeAttribute("aria-invalid");
  });
  const [mode, personality, difficulty] = settings(document);
  const launch = (role: "host" | "controller" | "spectator") => {
    const code = room.value.trim();
    if (!code) {
      roomError.textContent = "Enter a room code to create, join, or spectate a room.";
      room.setAttribute("aria-invalid", "true");
      room.focus();
      return;
    }
    const query = new URLSearchParams({ room: code, role });
    if (role === "host") {
      query.set("mode", mode.value);
      query.set("bot", personality.value);
      query.set("difficulty", difficulty.value);
    }
    location.assign(`${location.pathname}?${query}`);
  };
  const action = (label: string, role: "host" | "controller" | "spectator") => {
    const control = document.createElement("button");
    control.type = "button";
    control.textContent = label;
    control.addEventListener("click", () => launch(role));
    return control;
  };
  const controls = document.createElement("div");
  controls.className = "kf-online-actions";
  controls.append(
    action("Create room", "host"),
    action("Join room", "controller"),
    action("Spectate", "spectator"),
  );
  const recovery = document.createElement("details");
  recovery.className = "e308-infobox";
  const summary = document.createElement("summary");
  summary.textContent = "Recover a saved online room";
  recovery.append(summary, ...recoveryControls(document));
  section.append(
    heading,
    note,
    field(document, "Room code", room),
    roomError,
    field(document, "Match mode", mode),
    field(document, "Computer personality", personality),
    field(document, "Computer difficulty", difficulty),
    controls,
    recovery,
  );
  return section;
}

function field(document: Document, name: string, control: HTMLElement): HTMLLabelElement {
  const label = document.createElement("label");
  label.className = "kf-field";
  label.append(document.createTextNode(name), control);
  return label;
}

function settings(document: Document): [HTMLSelectElement, HTMLSelectElement, HTMLSelectElement] {
  const mode = select(document, "Online match mode", [
    ["Every Town for Itself", "every-town-for-itself"],
    ["Human team vs clockwork", "team-shift"],
  ]);
  const personality = select(document, "Online computer personality", [
    ["Seeded Mix", "seeded-mix"],
    ["Builder", "builder"],
    ["Defender", "defender"],
    ["Raider", "raider"],
    ["Siege Planner", "siege-planner"],
    ["Broker", "broker"],
  ]);
  const difficulty = select(document, "Online computer difficulty", [
    ["Apprentice", "apprentice"],
    ["Foreman", "foreman"],
    ["Superintendent", "superintendent"],
  ]);
  difficulty.value = "foreman";
  return [mode, personality, difficulty];
}

function recoveryControls(document: Document): readonly HTMLElement[] {
  const recovery = document.createElement("textarea");
  recovery.setAttribute("aria-label", "Retained online room save");
  recovery.placeholder = "Paste a majority-acknowledged room save";
  const recover = document.createElement("button");
  recover.type = "button";
  recover.textContent = "Recover save as new room";
  const errorNode = document.createElement("span");
  errorNode.setAttribute("role", "alert");
  recover.addEventListener("click", () => {
    try {
      const saved = decodeOnlineRoomSave(recovery.value);
      const code = `room-${crypto.randomUUID().slice(0, 8)}`;
      sessionStorage.setItem(`kilnfront-room-resume:${code}`, JSON.stringify(saved));
      location.assign(
        `${location.pathname}?${new URLSearchParams({ room: code, role: "host", resume: "1" })}`,
      );
    } catch (error) {
      recovery.setAttribute("aria-invalid", "true");
      errorNode.textContent = error instanceof Error ? error.message : "Invalid online room save";
    }
  });
  return [recovery, recover, errorNode];
}

function select(
  document: Document,
  label: string,
  options: readonly (readonly [string, string])[],
): HTMLSelectElement {
  const control = document.createElement("select");
  control.setAttribute("aria-label", label);
  for (const [text, value] of options) {
    const option = document.createElement("option");
    option.textContent = text;
    option.value = value;
    control.append(option);
  }
  return control;
}
/* v8 ignore stop */
